import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import _ from 'lodash';
import { Card, CardContent, Button } from '@lb-classroom/ui';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';
import StarRating from '../../UI/molecules/StarRating';
import appUtils from '../../../utils/appUtils';
import * as ModalActions from '../../../actions/modal';
import { withTranslation, WithTranslation } from 'next-i18next';

const msOfHalfAnHour = 1800000;

interface SlotStatus {
  status: string;
  timestamp: number;
}

interface TutorProfile {
  average_rating: number;
  review_count: number;
}

interface Tutor {
  id: number;
  name: string;
  profile_avatar_url: string;
  permalink_url?: string;
  tutor_profile: TutorProfile;
}

interface OnlineStatus {
  online: boolean;
}

interface TutorCardOwnProps {
  slotStatus?: SlotStatus[];
  timestampOfStart?: string | number;
  timestampOfEnd?: string | number;
  duration?: string | number;
  tutor?: Tutor;
  onlineStatus?: OnlineStatus;
}

interface TutorCardDispatchProps {
  modalActions: Record<string, (...args: unknown[]) => unknown>;
}

type TutorCardProps = TutorCardOwnProps &
  TutorCardDispatchProps &
  WithTranslation;

function mapStateToProps() {
  return {};
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    modalActions: bindActionCreators(
      ModalActions as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >,
      dispatch,
    ),
  };
}

const TutorCard = (props: TutorCardProps) => {
  const {
    t,
    tutor,
    duration,
    modalActions,
    slotStatus,
    timestampOfStart,
    timestampOfEnd,
    onlineStatus,
  } = props;

  const getAvailability = () => {
    if (slotStatus.length === 0) {
      return 0;
    }
    const startTs =
      typeof timestampOfStart === 'string'
        ? Number(timestampOfStart)
        : timestampOfStart;
    const endTs =
      typeof timestampOfEnd === 'string'
        ? Number(timestampOfEnd)
        : timestampOfEnd;
    let onSlots: SlotStatus[] = _.filter(
      slotStatus,
      (status) =>
        status.status === 'on' &&
        status.timestamp >= startTs &&
        status.timestamp <= endTs - msOfHalfAnHour,
    ) as SlotStatus[];
    if (duration === 1) {
      onSlots = convertToLongSlots(onSlots);
    }
    return onSlots.length;
  };

  const convertToLongSlots = (slotStatuses: SlotStatus[]): SlotStatus[] => {
    return _.filter(slotStatuses, (status) =>
      _.find(
        slotStatuses,
        (status2) => status2.timestamp === status.timestamp - msOfHalfAnHour,
      ),
    ) as SlotStatus[];
  };

  const showModal = () => {
    modalActions.show('bookingCalendar', {
      tutor,
      duration,
    });
  };

  const isOnline = !appUtils.isBlank(onlineStatus)
    ? onlineStatus.online
    : false;
  const tutorProfile = tutor.tutor_profile;
  const avatar = tutor.profile_avatar_url
    .replace('lingualbox-dev', 'lingualbox')
    .replace('lingualbox-staging-sg', 'lingualbox');
  const availability = getAvailability();
  const idOrPermalink = tutor.permalink_url
    ? tutor.permalink_url
    : tutor.id.toString();
  const rating = Math.round(tutorProfile.average_rating);
  const profileLink = `/tutors/${idOrPermalink}`;
  const onlineStatusClass = classnames(
    'w-4 h-4 rounded-full border-2 border-[#fafafa]',
    { 'bg-[#d3d3d3]': !isOnline },
    { 'bg-[#79c84d]': isOnline },
  );

  return (
    <div className="col-span-12 sm:col-span-6 md:col-span-6 lg:col-span-4">
      <Card className="shadow mb-4">
        <CardContent>
          <div className="text-center">
            <div className="tutor-image mb-8">
              <div className="inline-block relative">
                <div>
                  <Link legacyBehavior href="/tutors/[slug]" as={profileLink}>
                    <Image
                      height={100}
                      width={100}
                      alt="user-avatar"
                      className="rounded-full"
                      src={avatar}
                    />
                  </Link>
                </div>
                <div className="absolute right-[7%] bottom-[6%]">
                  <div className={onlineStatusClass}></div>
                </div>
              </div>
            </div>
            <div className="tutor-name">{tutor.name}</div>
            <div className="tutor-ratings mt-2 mb-4">
              <div className="flex items-center justify-center">
                <StarRating rating={rating} />
                <span className="ml-1 rating-count text-secondary">
                  {' '}
                  ({tutorProfile.review_count})
                </span>
              </div>
            </div>
            <div className="availability mb-4">
              <div className="title text-secondary">
                <span className="inline-block w-2 h-2 rounded-full bg-primary mr-1.5" />
                {t('student.tutorList.availability', {
                  count: availability,
                  smart_count: availability,
                })}
              </div>
            </div>
            <div className="tutor-panel-buttons">
              <div className="book-button-container mb-4 block">
                <Button onClick={showModal} className="w-40">
                  {t('links.bookQuickly')}
                </Button>
              </div>
              <div className="show-profile-button-continer block">
                <Link legacyBehavior href="/tutors/[slug]" as={profileLink}>
                  <Button variant="outline" className="w-40">
                    {t('links.viewProfile')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const wrapped = withTranslation('common')(TutorCard);

export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
